/**
 * FilterPanel Component - Phase 3: Search & Filter
 * 
 * Efficient filtering component with category/difficulty/time filters, URL state sync,
 * and mobile-optimized layout following atomic design principles.
 * 
 * REFACTORED for PROJECT_RULES.md Compliance:
 * - ✅ Component size: <300 lines (refactored from 340 lines)
 * - ✅ Modular design: Extracted FilterSection, FilterHeader components
 * - ✅ Single responsibility: Main orchestrator for filter layout
 * - ✅ Performance: React.memo, useMemo, useCallback optimizations
 * - ✅ Mobile: Touch targets ≥44px, responsive Flexbox layout
 * - ✅ TypeScript: Full type safety with proper interfaces
 * - ✅ Security: No user input without validation, no HTML injection
 * 
 * Module Structure:
 * - FilterPanel.tsx: Main component orchestrator (this file, <300 lines)
 * - FilterSection.tsx: Individual filter section component
 * - FilterHeader.tsx: Header with badge, clear button, collapse toggle
 * - All functionality maintained, cleaner separation of concerns
 */

import React, { useState, useCallback, useMemo } from 'react';
import FilterSection, { FilterOption } from './FilterSection';
import FilterHeader from './FilterHeader';

// Custom hook to create filter change handlers
const useFilterChangeHandlers = (
  filters: FilterState,
  onFiltersChange: (filters: FilterState) => void,
  onUrlSync?: (filters: FilterState) => void
) => {
  const handleCategoryChange = useCallback((categories: string[]) => {
    const newFilters = { ...filters, categories };
    onFiltersChange(newFilters);
    onUrlSync?.(newFilters);
  }, [filters, onFiltersChange, onUrlSync]);

  const handleDifficultyChange = useCallback((difficulties: string[]) => {
    const newFilters = { ...filters, difficulties };
    onFiltersChange(newFilters);
    onUrlSync?.(newFilters);
  }, [filters, onFiltersChange, onUrlSync]);

  const handleTimeRangeChange = useCallback((timeRanges: string[]) => {
    const newFilters = { ...filters, timeRanges };
    onFiltersChange(newFilters);
    onUrlSync?.(newFilters);
  }, [filters, onFiltersChange, onUrlSync]);

  const handleClearAll = useCallback(() => {
    const emptyFilters: FilterState = {
      categories: [],
      difficulties: [],
      timeRanges: [],
    };
    onFiltersChange(emptyFilters);
    onUrlSync?.(emptyFilters);
  }, [onFiltersChange, onUrlSync]);

  return {
    handleCategoryChange,
    handleDifficultyChange,
    handleTimeRangeChange,
    handleClearAll,
  };
};

// Helper function to render filter sections
const renderFilterSections = (
  filters: FilterState,
  categoryOptions: FilterOption[],
  difficultyOptions: FilterOption[],
  timeOptions: FilterOption[],
  showCounts: boolean,
  handlers: {
    handleCategoryChange: (categories: string[]) => void;
    handleDifficultyChange: (difficulties: string[]) => void;
    handleTimeRangeChange: (timeRanges: string[]) => void;
  }
) => (
  <div className="p-4 space-y-6">
    <FilterSection
      title="Category"
      options={categoryOptions}
      selectedValues={filters.categories}
      onSelectionChange={handlers.handleCategoryChange}
      showCounts={showCounts}
      ariaLabel="Filter by meal category"
    />
    <FilterSection
      title="Difficulty"
      options={difficultyOptions}
      selectedValues={filters.difficulties}
      onSelectionChange={handlers.handleDifficultyChange}
      showCounts={showCounts}
      ariaLabel="Filter by difficulty level"
    />
    <FilterSection
      title="Cooking Time"
      options={timeOptions}
      selectedValues={filters.timeRanges}
      onSelectionChange={handlers.handleTimeRangeChange}
      showCounts={showCounts}
      ariaLabel="Filter by cooking time"
    />
  </div>
);

// Filter types - extending existing search types
export interface FilterState {
  categories: string[];
  difficulties: string[];
  timeRanges: string[];
}

// Re-export FilterOption from FilterSection for backward compatibility
export type { FilterOption } from './FilterSection';

export interface FilterPanelProps {
  // Filter state management
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  
  // URL state management (for deep linking)
  onUrlSync?: (filters: FilterState) => void;
  
  // Filter options with result counts
  categoryOptions?: FilterOption[];
  difficultyOptions?: FilterOption[];
  timeOptions?: FilterOption[];
  
  // UI configuration
  className?: string;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
  showCounts?: boolean;
  
  // Performance optimization - handled by parent component
  // debounceMs is managed by useFilterState hook
}

// Filter option constants - following recipe domain patterns
const DEFAULT_CATEGORIES: FilterOption[] = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'dinner', label: 'Dinner' },
  { id: 'snack', label: 'Snack' },
  { id: 'dessert', label: 'Dessert' },
];

const DEFAULT_DIFFICULTIES: FilterOption[] = [
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
];

const DEFAULT_TIME_RANGES: FilterOption[] = [
  { id: 'under-15', label: '< 15 min' },
  { id: '15-30', label: '15-30 min' },
  { id: '30-60', label: '30-60 min' },
  { id: 'over-60', label: '60+ min' },
];

// FilterSection component extracted to separate file for modularity

/**
 * Main FilterPanel Component
 * 
 * Performance optimizations:
 * - React.memo to prevent unnecessary re-renders
 * - useMemo for expensive filter calculations
 * - useCallback for stable event handlers
 * - Minimal DOM updates with efficient state management
 */
export const FilterPanel: React.FC<FilterPanelProps> = React.memo(({
  filters,
  onFiltersChange,
  onUrlSync,
  categoryOptions = DEFAULT_CATEGORIES,
  difficultyOptions = DEFAULT_DIFFICULTIES,
  timeOptions = DEFAULT_TIME_RANGES,
  className = '',
  isCollapsible = true,
  defaultExpanded = false,
  showCounts = false,
}) => {
  // Local state for mobile collapsible behavior
  const [isExpanded, setIsExpanded] = useState(!isCollapsible || defaultExpanded);

  // Calculate total active filter count for badge display
  const activeFilterCount = useMemo(() => {
    return filters.categories.length + 
           filters.difficulties.length + 
           filters.timeRanges.length;
  }, [filters.categories.length, filters.difficulties.length, filters.timeRanges.length]);

  // Create filter change handlers using custom hook
  const { handleCategoryChange, handleDifficultyChange, handleTimeRangeChange, handleClearAll } = 
    useFilterChangeHandlers(filters, onFiltersChange, onUrlSync);

  // Toggle expanded state for mobile
  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Check if any filters are active for UI feedback
  const hasActiveFilters = useMemo(() => activeFilterCount > 0, [activeFilterCount]);

  return (
    <div className={`filter-panel bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      <FilterHeader
        activeFilterCount={activeFilterCount}
        hasActiveFilters={hasActiveFilters}
        isCollapsible={isCollapsible}
        isExpanded={isExpanded}
        onClearAll={handleClearAll}
        onToggleExpanded={handleToggleExpanded}
      />

      <div
        id="filter-content"
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
        aria-hidden={!isExpanded}
      >
        {renderFilterSections(
          filters,
          categoryOptions,
          difficultyOptions,
          timeOptions,
          showCounts,
          { handleCategoryChange, handleDifficultyChange, handleTimeRangeChange }
        )}
      </div>

      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {hasActiveFilters && `${activeFilterCount} filters active`}
        {!hasActiveFilters && 'No filters active'}
      </div>
    </div>
  );
});

FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;